import { useMutation, useQuery } from '@tanstack/react-query'
import {
  App,
  Button,
  Flex,
  Form,
  Modal,
  Slider,
  Typography,
  Upload
} from 'antd'
import type { RcFile } from 'antd/es/upload'
import type { UploadFile } from 'antd/lib'
import { RotateCw, UserRound } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { useTranslation } from 'react-i18next'

import {
  attachmentsDestroyMutation,
  attachmentsRetrieveOptions,
  organizationsAccessListQueryKey,
  usersListQueryKey,
  usersPartialUpdateMutation,
  usersRetrieveQueryKey,
  type UserRetrieve
} from 'src/api/main'
import {
  useAvatarUpload,
  useInvalidatePredicateQueries,
  useSessionContext
} from 'src/hooks'
import {
  isSvgFile,
  MAX_SVG_MB,
  storedPreviewUrl,
  SVG_ACCEPT,
  svgRejection,
  uploadFiles,
  useShowApiErrors
} from 'src/utils'

const { Dragger } = Upload
const { Text, Link } = Typography

type Props = {
  user: UserRetrieve
  userId: string
  trigger?: (open: () => void) => ReactNode
}

async function getCroppedBlob(
  imageSrc: string,
  cropArea: Area,
  rotation: number
): Promise<Blob> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!

  const radians = (rotation * Math.PI) / 180
  const sin = Math.abs(Math.sin(radians))
  const cos = Math.abs(Math.cos(radians))

  const rotatedWidth = image.width * cos + image.height * sin
  const rotatedHeight = image.width * sin + image.height * cos

  canvas.width = rotatedWidth
  canvas.height = rotatedHeight

  ctx.translate(rotatedWidth / 2, rotatedHeight / 2)
  ctx.rotate(radians)
  ctx.drawImage(image, -image.width / 2, -image.height / 2)

  const data = ctx.getImageData(
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height
  )

  canvas.width = cropArea.width
  canvas.height = cropArea.height
  ctx.putImageData(data, 0, 0)

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('Canvas toBlob failed'))
    }, 'image/jpeg')
  })
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', (e) => reject(e))
    img.crossOrigin = 'anonymous'
    img.src = url
  })
}

export const UploadAvatar = ({ user, userId, trigger }: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { user: sessionUser, updateUserData } = useSessionContext()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const {
    uploadFile,
    submitWebhook,
    isLoading: isUploading
  } = useAvatarUpload()

  const [modalOpen, setModalOpen] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<UploadFile[]>([])
  const [stagedPreviewUrl, setStagedPreviewUrl] = useState<string | null>(null)

  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  const isMe = sessionUser?.user.id === user.id
  const stagedOriginal = uploadedFile[0]?.originFileObj
  // A picked vector stays a vector: the cropper can only emit raster JPEG.
  const isSvg = isSvgFile(stagedOriginal)

  const { data: photoData, refetch: refetchPhoto } = useQuery({
    ...attachmentsRetrieveOptions({ path: { id: Number(user.photo?.id) } }),
    enabled: !!user.photo
  })

  const previewUrl =
    stagedPreviewUrl ?? (modalOpen ? (photoData?.file ?? null) : null)

  useEffect(() => {
    return () => {
      if (stagedPreviewUrl) {
        URL.revokeObjectURL(stagedPreviewUrl)
      }
    }
  }, [stagedPreviewUrl])

  const invalidate = () =>
    invalidateQueries([
      usersRetrieveQueryKey({ path: { id: Number(userId) } }),
      usersListQueryKey(),
      // The account/user hero shows `record.user.photo.file` (from the
      // workspace access list) in preference to the retrieve photo, so refetch
      // that member record too — otherwise the avatar stays stale until F5.
      organizationsAccessListQueryKey()
    ])

  const { mutateAsync: updateUser, isPending: isPendingUpdate } = useMutation({
    ...usersPartialUpdateMutation(),
    onError: (e) => showApiErrors(e)
  })

  const { mutateAsync: deleteAttachment, isPending: isPendingDelete } =
    useMutation({
      ...attachmentsDestroyMutation(),
      onError: (e) => showApiErrors(e)
    })

  const resetState = () => {
    setUploadedFile([])
    if (stagedPreviewUrl) {
      URL.revokeObjectURL(stagedPreviewUrl)
    }
    setStagedPreviewUrl(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(0)
    setCroppedAreaPixels(null)
    setModalOpen(false)
  }

  const beforeUpload = async (file: RcFile) => {
    if (!file.type.startsWith('image/') && !isSvgFile(file)) {
      message.error(t('account.avatar.only_images'))
      return Upload.LIST_IGNORE
    }
    // Only a vector skips the cropper's re-encoding, so only a vector needs vetting;
    // `uploadFiles` stores its sanitized copy, which is also what the preview shows.
    const svgProblem = await svgRejection(file)
    if (svgProblem) {
      message.error(t(`svgUpload.${svgProblem}`, { size: MAX_SVG_MB }))
      return Upload.LIST_IGNORE
    }
    const objectUrl = await storedPreviewUrl(file)
    setStagedPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return objectUrl
    })
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(0)
    return true
  }

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels)
  }, [])

  const buildCroppedFile = async (): Promise<File | null> => {
    if (!stagedPreviewUrl || !croppedAreaPixels) return null

    const croppedBlob = await getCroppedBlob(
      stagedPreviewUrl,
      croppedAreaPixels,
      rotation
    )
    // The cropper always emits JPEG, so the name must say so too: the backend
    // signs the upload's Content-Type from it.
    const baseName = (stagedOriginal?.name ?? '').replace(/\.[^.]+$/, '')

    return new File([croppedBlob], `${baseName || 'avatar'}.jpg`, {
      type: 'image/jpeg'
    })
  }

  const onSave = async () => {
    try {
      const fileToUpload = isSvg ? stagedOriginal : await buildCroppedFile()
      if (!fileToUpload) return

      const { id, upload_url, webhook_data } = await uploadFile(
        fileToUpload as unknown as RcFile
      )
      await uploadFiles(
        [
          {
            ...uploadedFile[0],
            originFileObj: fileToUpload as unknown as RcFile,
            response: { upload_url, webhook_data }
          }
        ],
        { submitWebhook, onError: (e) => showApiErrors(e) }
      )
      await updateUser({ path: { id: user.id }, body: { photo: id } })
      if (isMe) updateUserData()
      await invalidate()
      await refetchPhoto()
      message.success(t('account.avatar.upload_success'))
      resetState()
    } catch {
      // errors already shown via onError handlers
    }
  }

  const onDelete = async () => {
    try {
      await deleteAttachment({ path: { id: Number(user.photo?.id) } })
      await updateUser({ path: { id: user.id }, body: { photo: null } })
      if (isMe) updateUserData()
      await invalidate()
      message.success(t('account.avatar.delete_success'))
      resetState()
    } catch {
      // errors already shown via onError handlers
    }
  }

  const isBusy = isUploading || isPendingUpdate || isPendingDelete
  const hasExistingPhoto = !!user.photo
  const hasStagedFile = uploadedFile.length > 0

  const draggerContent = (
    <Flex vertical gap={8} align="center" style={{ padding: '20px 0' }}>
      <UserRound size={40} style={{ color: 'var(--ant-color-primary)' }} />
      <Text>
        {t('account.avatar.upload_description')}{' '}
        <Link>{t('account.avatar.upload_select')}</Link>
      </Text>
      <Text type="secondary">{t('account.avatar.upload_hint')}</Text>
    </Flex>
  )

  return (
    <>
      {trigger ? (
        trigger(() => setModalOpen(true))
      ) : (
        <Button type="default" onClick={() => setModalOpen(true)}>
          {t('account.avatar.change')}
        </Button>
      )}

      <Modal
        open={modalOpen}
        title={t('account.avatar.upload_title')}
        onCancel={resetState}
        width={480}
        footer={
          <Flex gap={8} justify="end">
            {hasStagedFile && (
              <Button type="primary" loading={isBusy} onClick={onSave}>
                {t('account.avatar.save')}
              </Button>
            )}
            {hasExistingPhoto && !hasStagedFile && (
              <Button danger loading={isBusy} onClick={onDelete}>
                {t('account.avatar.delete')}
              </Button>
            )}
            <Button onClick={resetState}>{t('cancel')}</Button>
          </Flex>
        }
        destroyOnHidden
      >
        {hasStagedFile && previewUrl ? (
          isSvg ? (
            <Flex vertical align="center" gap={12}>
              <img
                src={previewUrl}
                width={200}
                height={200}
                style={{ objectFit: 'contain', borderRadius: '50%' }}
                alt=""
              />
              <Text type="secondary">{t('account.avatar.svg_no_crop')}</Text>
            </Flex>
          ) : (
            <Flex vertical gap={12}>
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: 300,
                  background: 'var(--ant-color-fill-secondary)',
                  borderRadius: 8,
                  overflow: 'hidden'
                }}
              >
                <Cropper
                  image={previewUrl}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                />
              </div>

              <Flex gap={12} align="center">
                <Text style={{ whiteSpace: 'nowrap', minWidth: 48 }}>
                  {t('account.avatar.zoom')}
                </Text>
                <Slider
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={setZoom}
                  style={{ flex: 1 }}
                />
              </Flex>

              <Flex gap={12} align="center">
                <Text style={{ whiteSpace: 'nowrap', minWidth: 48 }}>
                  {t('account.avatar.rotate')}
                </Text>
                <Slider
                  min={0}
                  max={360}
                  step={1}
                  value={rotation}
                  onChange={setRotation}
                  style={{ flex: 1 }}
                />
                <Button
                  size="small"
                  icon={<RotateCw size={14} />}
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                />
              </Flex>
            </Flex>
          )
        ) : !hasExistingPhoto || hasStagedFile ? (
          <Form>
            <Form.Item
              name="photo"
              valuePropName="fileList"
              getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
            >
              <Dragger
                accept={`image/*,${SVG_ACCEPT}`}
                beforeUpload={beforeUpload}
                customRequest={(info) => info.onSuccess?.({})}
                maxCount={1}
                multiple={false}
                showUploadList={false}
                onChange={(info) => setUploadedFile(info.fileList.slice(-1))}
                style={{ width: '100%' }}
              >
                {draggerContent}
              </Dragger>
            </Form.Item>
          </Form>
        ) : (
          <Flex vertical align="center" gap={12}>
            <img
              src={photoData?.file ?? ''}
              width={200}
              height={200}
              style={{ objectFit: 'cover', borderRadius: '50%' }}
              alt=""
            />
            <Dragger
              accept={`image/*,${SVG_ACCEPT}`}
              beforeUpload={beforeUpload}
              customRequest={(info) => info.onSuccess?.({})}
              maxCount={1}
              multiple={false}
              showUploadList={false}
              onChange={(info) => setUploadedFile(info.fileList.slice(-1))}
              styles={{ root: { width: '100%' } }}
            >
              {draggerContent}
            </Dragger>
          </Flex>
        )}
      </Modal>
    </>
  )
}
